import { Link } from "react-router-dom";
import { Wifi, Wrench, FileWarning, MonitorSmartphone, Droplets, Network } from "lucide-react";
import Marquee from "@/components/Marquee";
import ServiceCard, { SERVICE_TIERS } from "@/components/ServiceCard";

const ISSUES = [
  {
    icon: FileWarning,
    title: "Paper Jams & Feed Errors",
    desc: "Jam codes, roller wear, misfeeds and duplex lockouts cleared on-site or guided remotely.",
  },
  {
    icon: Wifi,
    title: "Wi-Fi & Connectivity",
    desc: "Offline printer status, dropped wireless sync, router pairing and AirPrint setup.",
  },
  {
    icon: MonitorSmartphone,
    title: "Driver & Spooler Repair",
    desc: "Mac/Windows driver installs, print spooler hangs and queue errors fixed live.",
  },
  {
    icon: Droplets,
    title: "Print Quality & Ink Lines",
    desc: "Streaks, banding, clogged printhead nozzles and PIXMA ink line flushing.",
  },
  {
    icon: Network,
    title: "Office Network Printing",
    desc: "Shared printer setup, scan-to-email, and MAXIFY / imageRUNNER fleet maintenance.",
  },
  {
    icon: Wrench,
    title: "Hardware Maintenance",
    desc: "Paper path service, roller replacement and preventive care for busy office units.",
  },
];

export default function ServicesPage() {
  return (
    <main data-testid="services-page">
      {/* PAGE HERO */}
      <section className="py-16 px-6 max-w-6xl mx-auto border-b border-poster-border">
        <span className="px-2 py-0.5 bg-poster-accent text-white text-[11px] font-mono-lbl uppercase tracking-wider font-bold">
          Service Matrix
        </span>
        <h1 className="font-poster text-[clamp(40px,6vw,80px)] uppercase tracking-tight text-poster-ink leading-[0.95] my-4">
          <span className="ln"><span>Every printer fault,</span></span>
          <span className="ln"><span className="text-poster-accent">one fast desk.</span></span>
        </h1>
        <p className="text-base text-slate-600 leading-relaxed max-w-xl">
          Three clear support tiers for homes and small offices — local on-site dispatch, live remote sessions, and
          scheduled office maintenance. Upfront quotes before any repair proceeds.
        </p>
      </section>

      <Marquee />

      {/* TIERS */}
      <section className="py-20 px-6 max-w-6xl mx-auto border-b border-poster-border">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <span className="text-xs font-mono-lbl uppercase tracking-widest text-poster-accent font-bold">
              Solutions
            </span>
            <h2 className="font-poster text-4xl uppercase text-poster-ink mt-1">Independent Support Tiers</h2>
          </div>
          <p className="text-xs font-mono-lbl uppercase text-poster-muted mt-2 md:mt-0">
            Clear Pricing &bull; Fast Action
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICE_TIERS.map((tier) => (
            <ServiceCard key={tier.id} tier={tier} />
          ))}
        </div>
      </section>

      {/* ISSUE GRID */}
      <section className="py-20 px-6 max-w-6xl mx-auto border-b border-poster-border">
        <span className="text-xs font-mono-lbl uppercase tracking-widest text-poster-accent font-bold">
          Coverage
        </span>
        <h2 className="font-poster text-4xl uppercase text-poster-ink mt-1 mb-12">Faults We Fix Same-Day</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ISSUES.map((issue) => (
            <div
              key={issue.title}
              data-testid={`issue-card-${issue.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-white border-2 border-poster-ink shadow-[4px_4px_0px_#0F172A] p-6 hover:-translate-y-1 transition-transform"
            >
              <issue.icon className="text-poster-accent" size={28} strokeWidth={2.2} />
              <h3 className="font-poster text-xl uppercase text-poster-ink mt-4">{issue.title}</h3>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">{issue.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-poster-ink text-white py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <span className="text-xs font-mono-lbl uppercase tracking-widest text-poster-cyan font-bold">
            How It Works
          </span>
          <h2 className="font-poster text-4xl uppercase text-white mt-1 mb-12">Three Steps To A Working Printer</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Send The Form", d: "Tell us your model and the fault. Takes under a minute." },
              { n: "02", t: "Technician Responds", d: "An independent technician confirms your slot and quote upfront." },
              { n: "03", t: "Same-Day Fix", d: "Remote session starts fast, or a local technician arrives on-site." },
            ].map((step) => (
              <div key={step.n} data-testid={`process-step-${step.n}`} className="border-t-2 border-poster-cyan pt-6">
                <span className="font-poster text-5xl text-poster-cyan">{step.n}</span>
                <h3 className="font-poster text-2xl uppercase text-white mt-3">{step.t}</h3>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="inline-block px-7 py-3.5 bg-poster-accent hover:bg-sky-700 text-white font-mono-lbl text-xs uppercase tracking-widest font-bold shadow-md transition-colors"
            >
              Start Step 01 — Request Support &rarr;
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
